'use client';

import { c as _c } from "react/compiler-runtime";
import { type CryptoDepositOperation, unitsToDecimal } from '@morfi/onramp-sdk';
import type { useCryptoDeposit } from '@morfi/onramp-sdk/react';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
export function FundingCryptoStatus(t0) {
  const $ = _c(28);
  const {
    operation,
    actions
  } = t0;
  const t = useTranslations("onramp");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["source_detected", "processing"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const processing = t1.includes(operation.state);
  let t2;
  if ($[1] !== operation.state || $[2] !== t) {
    t2 = statusCopy(t, operation.state);
    $[1] = operation.state;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <div className="rounded-control bg-background-tertiary p-4 text-center"><h3 className="text-heading-sm">{t2}</h3></div>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== processing || $[7] !== t) {
    t4 = processing ? <ol className="flex flex-col gap-3"><TimelineStep label={t("crypto_deposit_state_source_detected")} complete={true} /><TimelineStep label={t("crypto_deposit_state_processing")} current={true} /><TimelineStep label={t("crypto_deposit_state_complete")} /></ol> : null;
    $[6] = processing;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== operation.transfers) {
    t5 = operation.transfers.length > 1 ? <div className="flex flex-col gap-2">{operation.transfers.map(_temp)}</div> : null;
    $[9] = operation.transfers;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== actions || $[12] !== operation.state || $[13] !== t) {
    t6 = operation.state === "instructions_expired" ? <AnimatedButton className="w-full" disabled={actions.getSnapshot().busy} onClick={() => {
      actions.reconcile();
    }}>{t("crypto_deposit_check_existing")}</AnimatedButton> : null;
    $[11] = actions;
    $[12] = operation.state;
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t) {
    t7 = t("powered_by");
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t7) {
    t8 = <span>{t7}</span>;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" rounded="" imageClassName="object-contain" className="h-4 w-16 bg-transparent" src="/assets/onramp/moonpay.svg" alt="MoonPay" />;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8) {
    t10 = <div className="flex items-center justify-center gap-2 text-caption text-foreground-secondary">{t8}{t9}</div>;
    $[20] = t8;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t3 || $[24] !== t4 || $[25] !== t5 || $[26] !== t6) {
    t11 = <div className="flex flex-col gap-4">{t3}{t4}{t5}{t6}{t10}</div>;
    $[22] = t10;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
}
function _temp(transfer, index) {
  return <div key={transfer.id} className="flex justify-between gap-4 rounded-control bg-background-tertiary p-3 text-body"><span>Transfer {index + 1}</span><span>{transfer.sourceUnits === null ? transfer.providerStatus : `${unitsToDecimal(BigInt(transfer.sourceUnits), transfer.sourceDecimals)} ${transfer.sourceAsset}`}</span></div>;
}
function TimelineStep(t0) {
  const $ = _c(7);
  const {
    label,
    complete: t1,
    current: t2
  } = t0;
  const complete = t1 === undefined ? false : t1;
  const current = t2 === undefined ? false : t2;
  const t3 = complete ? "\u2713" : current ? "\u25CC" : "\u25CB";
  let t4;
  if ($[0] !== t3) {
    t4 = <span className="flex size-6 items-center justify-center rounded-full bg-background-tertiary">{t3}</span>;
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== label) {
    t5 = <span>{label}</span>;
    $[2] = label;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== t4 || $[5] !== t5) {
    t6 = <li className="flex items-center gap-3 text-body">{t4}{t5}</li>;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
}
function statusCopy(t: ReturnType<typeof useTranslations<'onramp'>>, state: CryptoDepositOperation['state']): string {
  const copy = {
    source: t('crypto_deposit_state_source'),
    deposit_address: t('crypto_deposit_state_deposit_address'),
    checking_transfer: t('crypto_deposit_state_checking_transfer'),
    source_detected: t('crypto_deposit_state_source_detected'),
    below_minimum: t('crypto_deposit_state_below_minimum'),
    review_hold: t('crypto_deposit_state_review_hold'),
    processing: t('crypto_deposit_state_processing'),
    complete: t('crypto_deposit_state_complete'),
    instructions_expired: t('crypto_deposit_state_instructions_expired'),
    late_transfer: t('crypto_deposit_state_late_transfer'),
    multiple_transfers: t('crypto_deposit_state_multiple_transfers'),
    route_failed: t('crypto_deposit_state_route_failed'),
    refund: t('crypto_deposit_state_refund')
  } satisfies Record<CryptoDepositOperation['state'], string>;
  return copy[state];
}